const registeredDocks = new Map();
const DOCK_GAP = 4;
let layoutObserversInitialized = false;
let layoutUpdateFrame = null;
let sidebarAlignmentFrozenUntil = 0;
let sidebarSettleTimer = null;

export function registerDock(id, element, baseTop, sidebarSelector) {
  if (!id || !element) return;

  registeredDocks.set(id, {
    element,
    baseTop: Number.isFinite(baseTop) ? baseTop : 72,
    lastAlignedTop: Number.isFinite(baseTop) ? baseTop : 72,
    sidebarSelector: sidebarSelector || '',
  });

  initializeLayoutObservers();
  updateDockLayout();
}

export function setDockVisible(id, visible) {
  const entry = registeredDocks.get(id);
  if (!entry?.element) return;

  entry.element.style.display = visible ? 'inline-flex' : 'none';
  entry.element.classList.toggle('is-visible', visible);
  updateDockLayout();
}

function scheduleDockLayoutUpdate() {
  if (layoutUpdateFrame) return;

  layoutUpdateFrame = requestAnimationFrame(() => {
    layoutUpdateFrame = null;
    updateDockLayout();
  });
}

export function updateDockLayout() {
  const visibleEntries = [...registeredDocks.values()]
    .filter(entry => entry.element?.style.display !== 'none');

  const map = document.getElementById('map');
  const sidebar = document.getElementById('sidebar');
  const sidebarCollapsed = sidebar?.classList.contains('is-collapsed');
  const sidebarAlignmentFrozen = performance.now() < sidebarAlignmentFrozenUntil;
  const mapRect = map?.getBoundingClientRect();
  const mapRightOffset = Math.max(0, window.innerWidth - (mapRect?.right || window.innerWidth));
  const rowCounts = new Map();

  visibleEntries.forEach(entry => {
    const sidebarButton = entry.sidebarSelector
      ? document.querySelector(entry.sidebarSelector)
      : null;
    const buttonRect = sidebarButton?.getBoundingClientRect();
    const buttonStyle = sidebarButton ? window.getComputedStyle(sidebarButton) : null;
    const canUseSidebarButtonPosition =
      !sidebarCollapsed &&
      !sidebarAlignmentFrozen &&
      buttonRect?.width > 0 &&
      buttonRect?.height > 0 &&
      buttonStyle?.display !== 'none' &&
      buttonStyle?.visibility !== 'hidden';
    const dockHeight = entry.element.offsetHeight || 38;
    const dockWidth = entry.element.offsetWidth || 38;
    const top = canUseSidebarButtonPosition
      ? buttonRect.top + ((buttonRect.height - dockHeight) / 2)
      : entry.lastAlignedTop;
    const rowKey = entry.sidebarSelector || `top:${entry.baseTop}`;
    const rowIndex = rowCounts.get(rowKey) || 0;

    if (canUseSidebarButtonPosition) {
      entry.lastAlignedTop = top;
    }

    const nextTop = `${top}px`;
    const nextRight = `${mapRightOffset + (rowIndex * (dockWidth + DOCK_GAP))}px`;

    if (entry.element.style.top !== nextTop) {
      entry.element.style.top = nextTop;
    }

    if (entry.element.style.left !== 'auto') {
      entry.element.style.setProperty('left', 'auto', 'important');
    }

    if (entry.element.style.right !== nextRight) {
      entry.element.style.setProperty('right', nextRight, 'important');
    }
    rowCounts.set(rowKey, rowIndex + 1);
  });
}

function initializeLayoutObservers() {
  if (layoutObserversInitialized) return;
  layoutObserversInitialized = true;

  window.addEventListener('resize', scheduleDockLayoutUpdate);
  document.addEventListener('scroll', scheduleDockLayoutUpdate, true);

  const sidebar = document.getElementById('sidebar');
  if (!sidebar) return;

  new ResizeObserver(scheduleDockLayoutUpdate).observe(sidebar);
  new MutationObserver((records) => {
    const openedSidebar = records.some(record =>
      record.attributeName === 'class' &&
      sidebar.classList.contains('is-expanded') &&
      !String(record.oldValue || '').includes('is-expanded')
    );

    if (openedSidebar) {
      sidebarAlignmentFrozenUntil = performance.now() + 340;
      window.clearTimeout(sidebarSettleTimer);
      sidebarSettleTimer = window.setTimeout(scheduleDockLayoutUpdate, 360);
    }

    scheduleDockLayoutUpdate();
  }).observe(sidebar, {
    attributes: true,
    attributeFilter: ['class', 'style'],
    attributeOldValue: true,
  });
}
